import PropTypes from 'prop-types';

/**
 * SOC Controls Component
 * Provides navigation and control buttons for the SOC Dashboard carousel
 * 
 * @param {Object} props - Component props
 * @param {Function} props.onPrevious - Callback for previous section
 * @param {Function} props.onNext - Callback for next section
 * @param {Function} props.onTogglePause - Callback for pause/resume
 * @param {Function} props.onRefresh - Callback for manual refresh
 * @param {Function} props.onToggleFullscreen - Callback for fullscreen toggle
 * @param {boolean} props.isPaused - Whether auto-cycling is paused
 * @param {boolean} props.isFullscreen - Whether dashboard is in fullscreen mode
 */
const SOCControls = ({
  onPrevious,
  onNext,
  onTogglePause,
  onRefresh,
  onToggleFullscreen,
  isPaused,
  isFullscreen
}) => {
  return (
    <div className="soc-controls">
      <div className="controls-group navigation-controls">
        {/* Previous Section Button */}
        <button
          className="control-btn prev-btn"
          onClick={onPrevious}
          aria-label="Previous section"
          title="Previous section (← arrow key)"
        >
          <span className="btn-icon">←</span>
          <span className="btn-label">Previous</span>
        </button>

        {/* Pause/Resume Button */}
        <button
          className={`control-btn pause-btn ${isPaused ? 'paused' : 'playing'}`}
          onClick={onTogglePause}
          aria-label={isPaused ? 'Resume auto-cycling' : 'Pause auto-cycling'}
          title={isPaused ? 'Resume (Space)' : 'Pause (Space)'}
        >
          <span className="btn-icon">{isPaused ? '▶' : '⏸'}</span>
          <span className="btn-label">{isPaused ? 'Resume' : 'Pause'}</span>
        </button>

        {/* Next Section Button */}
        <button
          className="control-btn next-btn"
          onClick={onNext}
          aria-label="Next section"
          title="Next section (→ arrow key)"
        >
          <span className="btn-label">Next</span>
          <span className="btn-icon">→</span>
        </button>
      </div>

      <div className="controls-group action-controls">
        {/* Manual Refresh Button */}
        <button
          className="control-btn refresh-btn"
          onClick={onRefresh}
          aria-label="Refresh data"
          title="Refresh data manually"
        >
          <span className="btn-icon">↻</span>
          <span className="btn-label">Refresh</span>
        </button>

        {/* Fullscreen Toggle Button */}
        <button
          className="control-btn fullscreen-btn"
          onClick={onToggleFullscreen}
          aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
          title={isFullscreen ? 'Exit fullscreen (F11)' : 'Enter fullscreen (F11)'}
        >
          <span className="btn-icon">{isFullscreen ? '⛶' : '⛶'}</span>
          <span className="btn-label">{isFullscreen ? 'Exit FS' : 'Fullscreen'}</span>
        </button>
      </div>

      {/* Keyboard Shortcuts Info */}
      <div className="keyboard-shortcuts-hint">
        <small>
          ← → : Navigate | Space: Pause/Resume | F11: Fullscreen
        </small>
      </div>
    </div>
  );
};

SOCControls.propTypes = {
  onPrevious: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onTogglePause: PropTypes.func.isRequired,
  onRefresh: PropTypes.func.isRequired,
  onToggleFullscreen: PropTypes.func.isRequired,
  isPaused: PropTypes.bool.isRequired,
  isFullscreen: PropTypes.bool.isRequired
};

export default SOCControls;
